<script setup lang="ts">
import { onMounted, ref } from 'vue';
import SelectUserOverlay from '@/overlays/SelectUserOverlay.vue';
import EnterPinOverlay from '@/overlays/EnterPinOverlay.vue';
import SuccessOverlay from '@/overlays/SuccessOverlay.vue';
import { IDepartment, IProfile } from '@/interfaces';
import {
  DepartmentFragment,
  MealParticipantFragment,
} from '@/graphql/custom-types';

interface Props {
  title: string;
  titleBold?: string;
  participating?: boolean;
  multiSelect?: boolean;
  sharedDepartments?: (IDepartment | DepartmentFragment)[];
  participants?: (IProfile | MealParticipantFragment)[];
  employeeOnly?: boolean;
  titleSuccess?: string;
  limitedToProfile?: IProfile | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  participating: true,
  multiSelect: false,
  showSuccessOverlay: true,
  employeeOnly: false,
  titleSuccess: '',
});

onMounted(() => {
  selectedProfile.value = props.limitedToProfile || undefined;
  if (!selectedProfile.value) {
    showSelectUserOverlay.value = true;
  } else {
    showEnterPinOverlay.value = true;
  }
});

const emit = defineEmits(['close', 'success']);
const showSelectUserOverlay = ref<boolean>(false);
const showEnterPinOverlay = ref<boolean>(false);
const showSuccessOverlay = ref<boolean>(false);
const selectedProfile = ref<any>();

const profileSelected = (profile: IProfile) => {
  selectedProfile.value = profile;
  showEnterPinOverlay.value = true;
};

const handlePin = (pin: string) => {
  if (props.titleSuccess) {
    showSuccessOverlay.value = true;
  }

  emit('success', selectedProfile.value!, pin);
};

const closeFlow = () => {
  showSelectUserOverlay.value = false;
  selectedProfile.value = undefined;
  emit('close');
};

const closeShowEnterPinFlow = () => {
  showEnterPinOverlay.value = false;
  // If only one user is allowed close all overlays, otherwise only close enter pin overlay
  if (!props.limitedToProfile) {
    selectedProfile.value = undefined;
    return;
  }

  emit('close');
};
</script>

<template>
  <SelectUserOverlay
    v-if="showSelectUserOverlay"
    :participating="participating"
    :multi-select="multiSelect"
    :participants="participants"
    :shared-departments="sharedDepartments"
    :employee-only="employeeOnly"
    @close="closeFlow"
    @profile-selected="profileSelected"
  />

  <EnterPinOverlay
    v-if="showEnterPinOverlay"
    :title="title"
    :title-bold="titleBold"
    :profile="selectedProfile"
    :user-specific="limitedToProfile !== undefined"
    @close="closeShowEnterPinFlow"
    @success="handlePin"
  />

  <SuccessOverlay
    v-if="showSuccessOverlay"
    :title="titleSuccess"
    :duration="3000"
    @close="emit('close')"
  />
</template>
